import { useQueries } from "@tanstack/react-query";
import { useMemo } from "react";
import { AppInventoryItem } from "@/types/inventory";
import {
  AggregatedAppCveResult,
  aggregateAppCveResults,
  CveMatchResult,
  isMatchableApp,
  matchAppCves,
} from "@/services/appCveMatcher";

export interface AppScanProgress {
  loaded: number;
  total: number;
  done: boolean;
}

export interface UseAppCveMatchesResult {
  matchesByBundleId: Map<string, AggregatedAppCveResult>;
  /** Per-bundle scanning progress so the UI can show partial completion. */
  progressByBundleId: Map<string, AppScanProgress>;
  loadedCount: number;
  totalCount: number;
  isLoading: boolean;
  isError: boolean;
}

const STALE_24H = 24 * 60 * 60 * 1000;

interface VersionTask {
  bundleId: string;
  name: string;
  version: string;
}

export function useAppCveMatches(inventory: AppInventoryItem[]): UseAppCveMatchesResult {
  // Flatten inventory into one task per (app, version) so every detected
  // version is evaluated against NVD instead of just the first.
  const tasks = useMemo<VersionTask[]>(() => {
    const out: VersionTask[] = [];
    for (const app of inventory) {
      const versions = app.versions ?? [];
      for (const v of versions) {
        if (!isMatchableApp(app.bundle_id, v.version)) continue;
        out.push({ bundleId: app.bundle_id, name: app.name, version: v.version });
      }
    }
    return out;
  }, [inventory]);

  const queries = useQueries({
    queries: tasks.map((task) => ({
      queryKey: ["app-cve-match", task.bundleId, task.version],
      queryFn: () => matchAppCves(task.bundleId, task.version, task.name),
      staleTime: STALE_24H,
      gcTime: STALE_24H,
      retry: false,
    })),
  });

  return useMemo(() => {
    // Group successful per-version results by bundle id
    const perVersion = new Map<string, CveMatchResult[]>();
    const progress = new Map<string, AppScanProgress>();
    let loaded = 0;
    let errored = false;

    // Seed progress totals from tasks so apps with 0 settled queries still show 0/N.
    for (const task of tasks) {
      const cur = progress.get(task.bundleId) ?? { loaded: 0, total: 0, done: false };
      cur.total += 1;
      progress.set(task.bundleId, cur);
    }

    queries.forEach((q, i) => {
      const task = tasks[i];
      const cur = progress.get(task.bundleId)!;
      if (q.isSuccess && q.data) {
        const arr = perVersion.get(task.bundleId) ?? [];
        arr.push(q.data);
        perVersion.set(task.bundleId, arr);
        loaded++;
        cur.loaded += 1;
      } else if (q.isError) {
        errored = true;
        loaded++;
        cur.loaded += 1;
      }
    });

    // Mark per-app done flag
    progress.forEach((p) => {
      p.done = p.total > 0 && p.loaded >= p.total;
    });

    // Aggregate per bundle id (include partial results so UI can preview matches)
    const aggregated = new Map<string, AggregatedAppCveResult>();
    perVersion.forEach((results, bundleId) => {
      aggregated.set(bundleId, aggregateAppCveResults(bundleId, results));
    });

    return {
      matchesByBundleId: aggregated,
      progressByBundleId: progress,
      loadedCount: loaded,
      totalCount: tasks.length,
      isLoading: tasks.length > 0 && loaded < tasks.length,
      isError: errored,
    };
  }, [queries, tasks]);
}
